import { TimesheetState } from '../timesheet-collection.state';
import { withSortedEntries } from './sorted-entries';

/**
 * The slice invariant that a work entry only lives here once its confirmation was submitted.
 *
 * Entries of an open confirmation are derived at read time from the confirmation slice, so a stored
 * one is a leftover: either written by the push that used to copy them in, or by a build from before
 * that push was removed. Left in place they would be rendered *alongside* the derived ones, showing
 * the technician every in-progress entry twice and double-counting the hours.
 *
 * They cannot be de-duplicated against the derived entries instead, because the push minted a fresh
 * uuid per entry, so a stored id never matches a derived one. Nor can this be a one-shot migration:
 * the persistence layer carries no slice version to hang one off (`PersistedSlice` is a feature key
 * and a selector), and the timesheet slice rehydrates on every sync, not just at startup — so
 * anything short of a standing invariant would let storage put them straight back.
 */

/**
 * Whether any timesheet still holds a work entry whose confirmation is unsubmitted.
 *
 * Lets the hydrate effect decide whether the pruned collection has to be written back, the same way
 * it decides that for the retention window.
 *
 * @param timesheets the timesheets read back from storage
 * @returns whether anything would be pruned
 */
export function hasInProgressWorkEntries(timesheets: readonly TimesheetState[]): boolean {
    return timesheets.some((timesheet) =>
        timesheet.entries.some((entry) => entry.kind === 'work' && !entry.isConfirmationSubmitted),
    );
}

/**
 * Drops every work entry whose confirmation is unsubmitted, leaving manual entries alone.
 *
 * @param timesheets the timesheets read back from storage
 * @returns the timesheets holding only committed work entries, in chronological order
 */
export function withoutInProgressWorkEntries(timesheets: readonly TimesheetState[]): TimesheetState[] {
    return timesheets.map((timesheet) =>
        withSortedEntries({
            ...timesheet,
            entries: timesheet.entries.filter((entry) => entry.kind !== 'work' || entry.isConfirmationSubmitted),
        }),
    );
}
